import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, MailQuestion, Settings2, ArrowRight, TrendingUp, Activity } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { adminListOrders, adminListConsults } from "../lib/api";
import { formatINR } from "../lib/pricing";

export default function AdminOverviewPage() {
  const [stats, setStats] = useState({ orders: 0, paid: 0, revenue: 0, consults: 0, recent: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, c] = await Promise.all([adminListOrders({ limit: 200 }), adminListConsults({ limit: 200 })]);
        const orders = o.orders || [];
        const paid = orders.filter((x) => x.status === "paid");
        const revenue = paid.reduce((n, x) => n + (x.total_amount || 0), 0);
        setStats({ orders: orders.length, paid: paid.length, revenue, consults: (c.consults || []).length, recent: orders.slice(0, 5) });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AdminLayout>
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Studio admin</div>
      <h1 className="mt-2 font-serif-display text-[38px] font-medium leading-[0.98] tracking-tight sm:text-[48px] lg:text-[54px]">
        Overview<span className="text-brand">.</span>
      </h1>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-4">
        <Metric label="Orders" value={String(stats.orders)} to="/admin/orders" icon={ShoppingBag} />
        <Metric label="Paid" value={String(stats.paid)} accent to="/admin/orders?status=paid" icon={TrendingUp} />
        <Metric label="Revenue" value={loading ? "..." : formatINR(stats.revenue)} icon={TrendingUp} />
        <Metric label="Consults" value={String(stats.consults)} to="/admin/consults" icon={MailQuestion} />
      </div>

      <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px]">
        <div className="rounded-xl border border-brand-hair bg-brand-panel p-6">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Recent orders</div>
            <Link to="/admin/orders" className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand hover:underline">
              View all
            </Link>
          </div>
          <ul className="mt-4 divide-y divide-brand-hair/60">
            {stats.recent.length === 0 && (
              <li className="py-6 text-center text-[13px] text-brand-mute">No orders yet.</li>
            )}
            {stats.recent.map((o) => (
              <li key={o.id} className="grid grid-cols-[90px_1fr_auto] items-center gap-3 py-3 text-[13px] sm:grid-cols-[110px_1fr_auto_auto] sm:gap-4">
                <div className="truncate font-mono text-[11px] text-brand">{o.id}</div>
                <div className="min-w-0">
                  <div className="truncate font-serif-display text-[15px] text-brand-ivory">{o.customer_name}</div>
                  <div className="truncate font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">{o.customer_email}</div>
                </div>
                <StatusPill status={o.status} />
                <div className="col-span-3 -mt-1 font-mono text-[12.5px] text-brand-ivory sm:col-span-1 sm:mt-0">{formatINR(o.total_amount)}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-brand-hair bg-brand-panel p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Quick actions</div>
          <div className="mt-4 space-y-2">
            <QuickLink to="/admin/activity" icon={Activity} label="Lead Activity" hint="See who is viewing what right now" />
            <QuickLink to="/admin/content" icon={Settings2} label="Content & Pricing" hint="Edit price + image per service" />
            <QuickLink to="/admin/orders" icon={ShoppingBag} label="Orders" hint="Filter, search, export CSV" />
            <QuickLink to="/admin/consults" icon={MailQuestion} label="Consults" hint="Homepage popup submissions" />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Metric({ label, value, to, icon: Icon, accent }) {
  const body = (
    <div className={`rounded-xl border p-5 transition-colors ${accent ? "border-brand/50 bg-brand/[0.05]" : "border-brand-hair bg-brand-panel hover:border-brand/40"}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null} {label}
      </div>
      <div className={`mt-3 font-serif-display text-[38px] leading-none ${accent ? "text-brand" : "text-brand-ivory"}`}>{value}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function QuickLink({ to, icon: Icon, label, hint }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-left transition-colors hover:border-brand/60"
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand/10 text-brand">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-serif-display text-[15px] leading-tight text-brand-ivory">{label}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">{hint}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-brand-mute transition-colors group-hover:text-brand" />
    </Link>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: "bg-brand/15 text-brand",
    created: "border border-brand-hair text-brand-ivory/80",
    failed: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={`rounded-md px-2 py-1 font-mono text-[10px] uppercase tracking-[0.22em] ${map[status] || map.created}`}>
      {status || "created"}
    </span>
  );
}
